import React from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Truck, Target, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import StatCard from '../components/StatCard';
import Badge from '../components/Badge';
import { BarChart, LineChart } from '../components/Charts';
import { formatCurrency, formatDate, calculatePercentage } from '../utils/helpers';

export default function Dashboard() {
  const { state } = useApp();
  const { prospects, spk, delivery, team, settings } = state;

  // Stats
  const hotLeads = prospects.filter(p => p.status === 'Hot').length;
  const totalSPK = spk.length;
  const completedSPK = spk.filter(s => s.status === 'Selesai').length;
  const pendingDelivery = delivery.filter(d => d.statusPengiriman === 'Proses').length;
  const totalRevenue = spk.filter(s => s.status === 'Selesai').reduce((s, item) => s + item.harga, 0);
  const activeTeam = team.filter(t => t.status === 'active').length;
  const targetAchievement = calculatePercentage(completedSPK, settings.targetBulanan);

  // Chart data: SPK per month (mock based on existing data)
  const monthlyData = [
    { label: 'Sep', value: 3 },
    { label: 'Okt', value: 5 },
    { label: 'Nov', value: 4 },
    { label: 'Des', value: 7 },
    { label: 'Jan', value: totalSPK },
    { label: 'Feb', value: completedSPK },
  ];

  const revenueData = [
    { label: 'Sep', value: 850 },
    { label: 'Okt', value: 1200 },
    { label: 'Nov', value: 980 },
    { label: 'Des', value: 1500 },
    { label: 'Jan', value: Math.round(totalRevenue / 1000000) },
    { label: 'Feb', value: Math.round(totalRevenue / 900000) },
  ];

  const recentActivity = [
    ...prospects.slice(0, 3).map(p => ({ type: 'prospect', label: `Lead baru: ${p.nama}`, time: p.createdAt, status: p.status })),
    ...spk.slice(0, 2).map(s => ({ type: 'spk', label: `SPK: ${s.spkNumber} - ${s.namaPembeli}`, time: s.createdAt, status: s.status })),
  ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 6);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Selamat datang di Honda Sales Management System</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Target Bulan Ini</p>
          <p className="text-2xl font-bold text-red-600">{targetAchievement}%</p>
          <p className="text-xs text-gray-400">{completedSPK}/{settings.targetBulanan} unit</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Prospek" value={prospects.length} subtitle={`${hotLeads} Hot Leads`} icon={Users} color="red" trend="up" trendValue="+12% dari bulan lalu" />
        <StatCard title="SPK Aktif" value={totalSPK} subtitle={`${completedSPK} selesai`} icon={FileText} color="blue" />
        <StatCard title="Antrian Delivery" value={pendingDelivery} subtitle="unit dalam proses" icon={Truck} color="orange" />
        <StatCard title="Revenue" value={`Rp ${(totalRevenue / 1000000).toFixed(0)}M`} subtitle="bulan ini" icon={TrendingUp} color="green" trend="up" trendValue="+8% dari bulan lalu" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Penjualan Bulanan (Unit)</h3>
          <BarChart data={monthlyData} height={200} />
        </div>
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Trend Revenue (Juta Rp)</h3>
          <LineChart data={revenueData} height={200} />
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Aktivitas Terbaru</h3>
            <Link to="/prospecting" className="text-xs text-red-600 hover:underline">Lihat Semua</Link>
          </div>
          <div className="space-y-3">
            {recentActivity.map((act, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${act.type === 'prospect' ? 'bg-blue-100' : 'bg-green-100'}`}>
                  {act.type === 'prospect' ? <Users size={14} className="text-blue-600" /> : <FileText size={14} className="text-green-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{act.label}</p>
                  <p className="text-xs text-gray-400">{formatDate(act.time)}</p>
                </div>
                <Badge status={act.status} />
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Tim Sales</h3>
          <div className="space-y-3">
            {team.slice(0, 4).map(member => (
              <div key={member.id} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-red-600 text-xs font-bold">{member.nama.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">{member.nama}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full">
                      <div className="h-1.5 bg-red-500 rounded-full transition-all" style={{ width: `${Math.min(calculatePercentage(member.pencapaian, member.target), 100)}%` }} />
                    </div>
                    <span className="text-xs text-gray-400">{member.pencapaian}/{member.target}</span>
                  </div>
                </div>
                <Badge status={member.status} />
              </div>
            ))}
          </div>
          <Link to="/team" className="block mt-4 text-center text-xs text-red-600 hover:underline">
            Lihat Semua Tim →
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Aksi Cepat</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Tambah Prospek', icon: Users, path: '/prospecting', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
            { label: 'Buat SPK', icon: FileText, path: '/spk', color: 'bg-green-50 text-green-700 hover:bg-green-100' },
            { label: 'Cek Delivery', icon: Truck, path: '/delivery', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
            { label: 'Lihat Laporan', icon: Target, path: '/reports', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
          ].map(({ label, icon: Icon, path, color }) => (
            <Link key={path} to={path} className={`flex items-center gap-3 p-3 rounded-xl ${color} transition-colors`}>
              <Icon size={18} />
              <span className="text-sm font-medium">{label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
